import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, ThemeConfig } from '@portfolio/shared';

// ── Auth Slice ─────────────────────────────────────────────────────────────────
interface AuthState {
  user: Omit<User, 'createdAt' | 'updatedAt'> | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const initialAuthState: AuthState = {
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isLoading: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    setTokens(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
    setUser(state, action: PayloadAction<AuthState['user']>) {
      state.user = action.payload;
    },
    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    updateTokens(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
  },
});

// ── Theme Slice ────────────────────────────────────────────────────────────────
interface ThemeState {
  config: Partial<ThemeConfig> | null;
  isLoaded: boolean;
}

const themeSlice = createSlice({
  name: 'theme',
  initialState: { config: null, isLoaded: false } as ThemeState,
  reducers: {
    setTheme(state, action: PayloadAction<Partial<ThemeConfig>>) {
      state.config = action.payload;
      state.isLoaded = true;
      // Apply CSS variables
      const root = document.documentElement;
      const t = action.payload;
      if (t.primaryColor)    root.style.setProperty('--color-primary', t.primaryColor);
      if (t.secondaryColor)  root.style.setProperty('--color-secondary', t.secondaryColor);
      if (t.accentColor)     root.style.setProperty('--color-accent', t.accentColor);
      if (t.backgroundColor) root.style.setProperty('--color-bg', t.backgroundColor);
      if (t.surfaceColor)    root.style.setProperty('--color-surface', t.surfaceColor);
      if (t.textColor)       root.style.setProperty('--color-text', t.textColor);
      if (t.fontFamily)      root.style.setProperty('--font-family', t.fontFamily);
      if (t.borderRadius)    root.style.setProperty('--border-radius', t.borderRadius);
    },
  },
});

// ── UI Slice ───────────────────────────────────────────────────────────────────
interface UiState {
  sidebarOpen: boolean;
  activeSection: string;
  language: string;
}

const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    sidebarOpen: true,
    activeSection: 'hero',
    language: localStorage.getItem('i18nextLng') || 'en',
  } as UiState,
  reducers: {
    toggleSidebar(state) { state.sidebarOpen = !state.sidebarOpen; },
    setSidebarOpen(state, action: PayloadAction<boolean>) { state.sidebarOpen = action.payload; },
    setActiveSection(state, action: PayloadAction<string>) { state.activeSection = action.payload; },
    setLanguage(state, action: PayloadAction<string>) { state.language = action.payload; },
  },
});

// ── Store ──────────────────────────────────────────────────────────────────────
export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    theme: themeSlice.reducer,
    ui: uiSlice.reducer,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const { setTokens, setUser, logout, setLoading, updateTokens } = authSlice.actions;
export const { setTheme } = themeSlice.actions;
export const { toggleSidebar, setSidebarOpen, setActiveSection, setLanguage } = uiSlice.actions;
